import { CreateRecyclingConsignmentOrderV2SchemaType } from '../../common';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Control, useController, useWatch } from 'react-hook-form';
import authenticationApi from 'apis/authentication';
import { Button, Image, message, Spin } from 'antd';
import ProxiedImage from 'components/proxied-image';
import { AlertTriangle, ChevronRight, X } from 'lucide-react';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import AuthenticationDetailModal from './authentication-detail-modal';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import findCascaderOptionById from 'utils/findCascaderOptionById';
import formatTime from 'utils/formatTime';
import { CascaderOption, SelectOption } from 'types/base';
import { clearAiIdentifyTransientState } from './ai-identify-pics-state';
import {
  AuthenticationStatus,
  AuthenticationResult,
  ZoneImageState,
  PhotoZoneConfig,
  AI_IDENTIFY_ACCEPT,
  AI_IDENTIFY_ALLOWED_FORMATS,
  normalizeBagType,
  getBagTypeConfig,
  getBagTypeNameFromCategoryIdList,
  deriveSeriesName,
  getVerdictStyle,
} from './authentication-config';
import {
  getAuthenticationDisplaySummary,
  getAuthenticationDisplayVerdictText,
} from './authentication-display';
import { extractClipboardImageFiles } from 'pages/rrs/recycling-consignment/modal-components/clipboard-image';
import {
  getBoundAuthenticationSummary,
  shouldClearAuthenticationBagIdOnBagTypeChange,
  shouldShowBoundAuthenticationSummary,
} from './ai-identify-pics.utils';
import {
  AuthenticationUploadIdError,
  getAuthenticationUploadIds,
  lockAuthenticationUploadIds,
} from './ai-identify-upload-session';

const mapSelectValuesToValues = (
  values: Array<string | number> | undefined,
  options: SelectOption[],
) =>
  (values || [])
    .map((value) => options.find((option) => option.value === value)?.value)
    .filter(Boolean)
    .map((label) => String(label));

const mapMaterialValuesToValues = (
  material: string[][] | undefined,
  options: CascaderOption[],
) =>
  (material || [])
    .map((path) => path[path.length - 1])
    .map((value) => findCascaderOptionById(value, options)?.value)
    .filter(Boolean)
    .map((label) => String(label));

const AI_IDENTIFY_QUERY_INTERVAL_MS = 30000;

export type AiIdentifyPicsRef = {
  clearLocalPreviews: () => void;
};

const revokeBlobUrl = (url?: string) => {
  if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
};

const resolvePreviewUrl = (zoneState?: ZoneImageState[string]) =>
  zoneState?.localPreviewUrl || zoneState?.backendPath || '';

type AiIdentifyPicsProps = {
  control: Control<CreateRecyclingConsignmentOrderV2SchemaType>;
  index: number;
  bagTypeName?: string;
};

type BoundAuthenticationSummary = {
  displayNo: string;
  verdict: string | null;
  verdictKind:
    | 'service'
    | 'insufficient'
    | 'few-sample-positive'
    | 'uncertain'
    | 'genuine'
    | 'suspected-fake'
    | 'none';
  score?: number | null;
  scoreKind: 'numeric' | 'insufficient' | 'none';
  mode: 'normal' | 'insufficient' | 'few-sample-positive';
  createdAt: string;
};

const AiIdentifyPics = forwardRef<AiIdentifyPicsRef, AiIdentifyPicsProps>(
  ({ control, index, bagTypeName: bagTypeNameProp }, ref) => {
    const bagIdRef = useRef<string | undefined>(undefined);
    const authenticationBagIdRef = useRef<number | undefined>(undefined);
    const authenticationIdRef = useRef<string | undefined>(undefined);
    const queryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const firstUploadGateRef = useRef<Promise<void> | null>(null);
    const firstUploadGateResolveRef = useRef<(() => void) | null>(null);
    const uploadIdContractFailedRef = useRef(false);
    const zoneImagesRef = useRef<ZoneImageState>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [authStatus, setAuthStatus] = useState<AuthenticationStatus>('idle');
    const [authResult, setAuthResult] = useState<AuthenticationResult>({});
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [zoneImages, setZoneImages] = useState<ZoneImageState>({});
    const [dragOverZoneId, setDragOverZoneId] = useState<string | null>(null);
    const [boundSummaryLoading, setBoundSummaryLoading] = useState(false);
    const [boundSummary, setBoundSummary] =
      useState<BoundAuthenticationSummary | null>(null);

    useEffect(() => {
      zoneImagesRef.current = zoneImages;
    }, [zoneImages]);

    useEffect(() => {
      return () => {
        if (queryTimerRef.current) {
          clearTimeout(queryTimerRef.current);
          queryTimerRef.current = null;
        }
        Object.values(zoneImagesRef.current).forEach((state) => {
          revokeBlobUrl(state?.localPreviewUrl);
        });
      };
    }, []);

    const invalidateAuthenticationResult = useCallback(() => {
      if (queryTimerRef.current) {
        clearTimeout(queryTimerRef.current);
        queryTimerRef.current = null;
      }
      authenticationIdRef.current = undefined;
      setAuthStatus('idle');
      setAuthResult({});
      setIsSubmitting(false);
    }, []);

    const { field: authenticationBagIdField } = useController({
      name: `productList.${index}.authenticationBagId`,
      control,
    });

    const product = useWatch({
      control,
      name: `productList.${index}`,
    });
    const {
      colorSelectOptions,
      hardwareSelectOptions,
      accessorySelectOptions,
      materialCascaderOptionsMap,
      productCategoryCascaderOptions,
    } = useAppSelector(selectGlobalInfo);

    // 使用传入的 bagTypeName，如果没有传入则自己计算（向后兼容）
    const bagTypeName = useMemo(
      () =>
        bagTypeNameProp ??
        getBagTypeNameFromCategoryIdList(
          product?.productCategoryIdList,
          productCategoryCascaderOptions,
        ),
      [
        bagTypeNameProp,
        product?.productCategoryIdList,
        productCategoryCascaderOptions,
      ],
    );

    const localPreviewByOriginalName = useMemo(() => {
      return Object.values(zoneImages).reduce<Record<string, string>>(
        (acc, zoneState) => {
          const originalName = zoneState?.originalName;
          const previewUrl = resolvePreviewUrl(zoneState);
          if (originalName && previewUrl) {
            acc[originalName] = previewUrl;
          }
          return acc;
        },
        {},
      );
    }, [zoneImages]);

    const prevBagTypeRef = useRef<string | undefined>(bagTypeName);
    const prevAuthenticationBagIdRef = useRef<number | undefined>(
      authenticationBagIdField.value,
    );
    const hasZoneImages = Object.keys(zoneImages).length > 0;
    const hasAuthenticationTaskState =
      authStatus !== 'idle' || isSubmitting || !!authenticationIdRef.current;
    const shouldShowBoundSummary = shouldShowBoundAuthenticationSummary({
      authenticationBagId: authenticationBagIdField.value,
      hasLocalBagId: !!bagIdRef.current,
      hasZoneImages,
      hasAuthenticationTaskState,
    });

    useImperativeHandle(
      ref,
      () => ({
        clearLocalPreviews: () => {
          setZoneImages((prev) =>
            clearAiIdentifyTransientState(prev, revokeBlobUrl),
          );
        },
      }),
      [],
    );

    // 包型切换时重置状态
    useEffect(() => {
      const currentBagType = bagTypeName;
      // 只在包型实际变化时重置（跳过初始化）
      if (prevBagTypeRef.current !== currentBagType) {
        const shouldClearAuthenticationBagId =
          shouldClearAuthenticationBagIdOnBagTypeChange({
            previousBagTypeName: prevBagTypeRef.current,
            currentBagTypeName: currentBagType,
            previousAuthenticationBagId: prevAuthenticationBagIdRef.current,
            currentAuthenticationBagId: authenticationBagIdField.value,
          });
        prevBagTypeRef.current = currentBagType;
        // 清除定时器
        if (queryTimerRef.current) {
          clearTimeout(queryTimerRef.current);
          queryTimerRef.current = null;
        }
        // 重置所有状态
        Object.values(zoneImagesRef.current).forEach((state) => {
          revokeBlobUrl(state?.localPreviewUrl);
        });
        setZoneImages({});
        setAuthStatus('idle');
        setAuthResult({});
        setIsSubmitting(false);
        // 重置 refs
        bagIdRef.current = undefined;
        authenticationBagIdRef.current = undefined;
        authenticationIdRef.current = undefined;
        uploadIdContractFailedRef.current = false;
        // 重置表单字段
        if (shouldClearAuthenticationBagId) {
          authenticationBagIdField.onChange(undefined);
        }
      }
    }, [bagTypeName, authenticationBagIdField]);

    useEffect(() => {
      prevAuthenticationBagIdRef.current = authenticationBagIdField.value;
    }, [authenticationBagIdField.value]);

    useEffect(() => {
      authenticationBagIdRef.current = authenticationBagIdField.value;
    }, [authenticationBagIdField.value]);

    useEffect(() => {
      if (!shouldShowBoundSummary || !authenticationBagIdField.value) {
        setBoundSummaryLoading(false);
        setBoundSummary(null);
        return;
      }

      let cancelled = false;

      const loadBoundSummary = async () => {
        setBoundSummaryLoading(true);
        try {
          const detail = await authenticationApi.getAuthenticationDetail(
            String(authenticationBagIdField.value),
          );
          if (cancelled) return;
          setBoundSummary(
            getBoundAuthenticationSummary({
              latestTask: detail?.latestTask,
              latestResult: detail?.latestResult,
              detectionPoints: detail?.detectionPoints,
            }),
          );
        } catch (error: any) {
          if (!cancelled && !error?._aiServiceHandled) {
            message.error(String(i18n.t(LOCALS.ai_identify_result_fetch_failed)));
          }
          if (!cancelled) {
            setBoundSummary(null);
          }
        } finally {
          if (!cancelled) {
            setBoundSummaryLoading(false);
          }
        }
      };

      loadBoundSummary();

      return () => {
        cancelled = true;
      };
    }, [authenticationBagIdField.value, shouldShowBoundSummary]);

    // 查询鉴定结果
    const fetchAuthenticationResult = useCallback(async () => {
      const bagId = authenticationBagIdRef.current;
      if (!bagId) return;

      try {
        const detail = await authenticationApi.getAuthenticationDetail(String(bagId));
        // Check if authenticationBagId hasn't changed during the async call
        if (authenticationBagIdRef.current !== bagId) return;

        const task = detail?.latestTask;
        const result = detail?.latestResult;

        if (task?.status === 'completed' && result) {
          const displaySummary = getAuthenticationDisplaySummary({
            latestResult: result,
            detectionPoints: detail?.detectionPoints,
          });
          message.success(String(i18n.t(LOCALS.ai_identify_completed)));
          setAuthStatus('completed');
          if (queryTimerRef.current) {
            clearTimeout(queryTimerRef.current);
            queryTimerRef.current = null;
          }
          setAuthResult({
            verdict: getAuthenticationDisplayVerdictText(
              displaySummary,
              i18n.t.bind(i18n),
            ),
            score: displaySummary.displayScore ?? undefined,
            mode: displaySummary.mode,
            scoreKind: displaySummary.displayScoreKind,
            verdictKind: displaySummary.displayVerdictKind,
          });
        } else if (task?.status === 'failed') {
          message.error(String(i18n.t(LOCALS.ai_identify_failed)));
          setAuthStatus('failed');
          if (queryTimerRef.current) {
            clearTimeout(queryTimerRef.current);
            queryTimerRef.current = null;
          }
        } else {
          // Still processing — schedule next poll
          if (queryTimerRef.current) {
            clearTimeout(queryTimerRef.current);
          }
          queryTimerRef.current = setTimeout(() => {
            fetchAuthenticationResult();
          }, AI_IDENTIFY_QUERY_INTERVAL_MS);
        }
      } catch (error: any) {
        // Network error — retry
        if (queryTimerRef.current) {
          clearTimeout(queryTimerRef.current);
        }
        queryTimerRef.current = setTimeout(() => {
          fetchAuthenticationResult();
        }, AI_IDENTIFY_QUERY_INTERVAL_MS);
      }
    }, []);

    const buildBagMeta = useCallback(() => {
      if (!product) return undefined;
      const nameEn = product.name?.en?.trim();
      const sizeEn = product.attrSize?.en?.trim();
      const names = normalizeBagType(bagTypeName) || deriveSeriesName(nameEn);

      const colorLabels = mapSelectValuesToValues(
        product.color,
        colorSelectOptions,
      );
      const hardwareLabels = mapSelectValuesToValues(
        product.hardware,
        hardwareSelectOptions,
      );
      const accessoryLabels = mapSelectValuesToValues(
        product.accessory,
        accessorySelectOptions,
      );
      const materialOptions =
        product.productCategoryIdList?.length &&
        materialCascaderOptionsMap[product.productCategoryIdList[0]]
          ? materialCascaderOptionsMap[product.productCategoryIdList[0]]
          : [];
      const materialLabels = mapMaterialValuesToValues(
        product.material,
        materialOptions,
      );

      const payload = {
        brand: product.brandName,
        names,
        name: nameEn,
        color: colorLabels.length ? colorLabels.join(',') : undefined,
        rank: product.rank,
        material: materialLabels.length ? materialLabels.join(',') : undefined,
        hardware: hardwareLabels.length ? hardwareLabels.join(',') : undefined,
        stamp: product.stamp || undefined,
        accessory: accessoryLabels.length
          ? accessoryLabels.join(',')
          : undefined,
        size: sizeEn || undefined,
      };

      const hasValue = Object.values(payload).some(
        (value) => value !== undefined && value !== '',
      );
      return hasValue ? payload : undefined;
    }, [
      accessorySelectOptions,
      bagTypeName,
      colorSelectOptions,
      hardwareSelectOptions,
      materialCascaderOptionsMap,
      product,
    ]);

    const isBagMetaComplete = useMemo(() => {
      const meta = buildBagMeta();
      if (!meta) return false;
      return !!(
        meta.brand &&
        (meta.names || meta.name) &&
        meta.color &&
        meta.rank &&
        meta.material &&
        meta.hardware &&
        meta.stamp &&
        meta.accessory
      );
    }, [buildBagMeta]);

    // 处理区域图片上传
    const handleZoneUpload = useCallback(
      async (zoneId: string, file: File) => {
        // 文件格式验证
        if (!AI_IDENTIFY_ALLOWED_FORMATS.includes(file.type)) {
          message.error(String(i18n.t(LOCALS.ai_identify_file_format_error)));
          return;
        }

        // 文件大小验证 (10MB = 10 * 1024 * 1024 bytes)
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
          message.error(String(i18n.t(LOCALS.ai_identify_file_size_limit)));
          return;
        }

        // 首次上传需要同时拿到两个 ID，其他并发上传需等待首张完成。
        while (
          (!bagIdRef.current || !authenticationBagIdRef.current) &&
          firstUploadGateRef.current
        ) {
          await firstUploadGateRef.current;
        }

        let isFirstUploadOwner = false;
        if (
          !bagIdRef.current &&
          !authenticationBagIdRef.current &&
          !firstUploadGateRef.current
        ) {
          isFirstUploadOwner = true;
          firstUploadGateRef.current = new Promise<void>((resolve) => {
            firstUploadGateResolveRef.current = () => resolve();
          });
        }

        setZoneImages((prev) => ({
          ...prev,
          [zoneId]: { ...prev[zoneId], uploading: true },
        }));

        try {
          if (uploadIdContractFailedRef.current) {
            throw new AuthenticationUploadIdError(
              'AI authentication upload session is invalid',
            );
          }
          const lockedIds = getAuthenticationUploadIds(
            bagIdRef.current,
            authenticationBagIdRef.current,
          );
          const bagMeta = buildBagMeta();

          const authUpload = await authenticationApi.uploadSingle({
            file,
            type: 'bag',
            bagId: lockedIds?.bagId,
            bagMeta,
            authenticationBagId: lockedIds?.authenticationBagId,
          });

          const nextIds = lockAuthenticationUploadIds(lockedIds, {
            bagId: authUpload?.data?.bagId,
            authenticationBagId: authUpload?.data?.authenticationBagId,
          });
          bagIdRef.current = nextIds.bagId;
          authenticationBagIdRef.current = nextIds.authenticationBagId;
          authenticationBagIdField.onChange(nextIds.authenticationBagId);

          // 更新区域图片状态
          setZoneImages((prev) => {
            revokeBlobUrl(prev[zoneId]?.localPreviewUrl);
            return {
              ...prev,
              [zoneId]: {
                ...prev[zoneId],
                imageId: authUpload.data.savedImageId || prev[zoneId]?.imageId,
                backendPath: authUpload.data.backendPath,
                type: authUpload.data.type,
                originalName: authUpload.data.originalName,
                publicUrl: '',
                file,
                localPreviewUrl:
                  file.type === 'image/heic' || file.type === 'image/heif'
                    ? undefined
                    : URL.createObjectURL(file),
                uploading: false,
              },
            };
          });
          invalidateAuthenticationResult();
        } catch (error: any) {
          if (error instanceof AuthenticationUploadIdError) {
            uploadIdContractFailedRef.current = true;
          }
          if (!error._aiServiceHandled) {
            message.error(String(i18n.t(LOCALS.ai_identify_upload_failed)));
          }
          setZoneImages((prev) => ({
            ...prev,
            [zoneId]: { ...prev[zoneId], uploading: false },
          }));
        } finally {
          if (isFirstUploadOwner) {
            firstUploadGateResolveRef.current?.();
            firstUploadGateResolveRef.current = null;
            firstUploadGateRef.current = null;
          }
        }
      },
      [buildBagMeta, authenticationBagIdField, invalidateAuthenticationResult],
    );

    // 处理区域图片删除
    const handleZoneDelete = useCallback(async (zoneId: string) => {
      const zoneState = zoneImagesRef.current[zoneId];
      if (!zoneState) return;

      if (zoneState.backendPath) {
        setZoneImages((prev) => ({
          ...prev,
          [zoneId]: { ...prev[zoneId], uploading: true },
        }));

        try {
          let imageId = zoneState.imageId;
          if (!imageId && authenticationBagIdRef.current) {
            const detail = await authenticationApi.getAuthenticationDetail(
              String(authenticationBagIdRef.current),
            );
            imageId = detail?.images?.find(
              (image) =>
                image.backendPath === zoneState.backendPath &&
                image.originalName === zoneState.originalName,
            )?.id;
          }

          if (!imageId) {
            message.error(String(i18n.t(LOCALS.ai_identify_delete_failed)));
            setZoneImages((prev) => ({
              ...prev,
              [zoneId]: { ...prev[zoneId], uploading: false },
            }));
            return;
          }

          await authenticationApi.deleteImage(imageId);
        } catch (error: any) {
          if (!error._aiServiceHandled) {
            message.error(String(i18n.t(LOCALS.ai_identify_delete_failed)));
          }
          setZoneImages((prev) => ({
            ...prev,
            [zoneId]: { ...prev[zoneId], uploading: false },
          }));
          return;
        }
      }

      setZoneImages((prev) => {
        revokeBlobUrl(prev[zoneId]?.localPreviewUrl);
        const newState = { ...prev };
        delete newState[zoneId];
        return newState;
      });
      invalidateAuthenticationResult();
    }, [invalidateAuthenticationResult]);

    // 获取当前包型（根据 productCategoryIdList 推导）
    const currentBagType = normalizeBagType(bagTypeName);
    const bagTypeConfig = getBagTypeConfig(currentBagType);

    // 检查必填项是否全部上传
    const checkRequiredZones = useCallback(() => {
      if (!bagTypeConfig) return false;
      const requiredZones = bagTypeConfig.filter((zone) => zone.required);
      return requiredZones.every(
        (zone) =>
          zoneImages[zone.id]?.backendPath && !zoneImages[zone.id]?.uploading,
      );
    }, [bagTypeConfig, zoneImages]);

    // 收集所有已上传的图片路径（按配置顺序）
    const collectImagePaths = useCallback(() => {
      if (!bagTypeConfig) return [];
      return bagTypeConfig
        .map((zone) => zoneImages[zone.id]?.backendPath)
        .filter((path): path is string => !!path);
    }, [bagTypeConfig, zoneImages]);

    // 收集所有已上传的图片 URL（按配置顺序）
    const collectImageUrls = useCallback(() => {
      if (!bagTypeConfig) return [];
      return bagTypeConfig
        .map((zone) => resolvePreviewUrl(zoneImages[zone.id]))
        .filter((url): url is string => !!url);
    }, [bagTypeConfig, zoneImages]);

    // 开始AI鉴定
    const handleStartAuthentication = useCallback(async () => {
      const imagePaths = collectImagePaths();
      const allRequiredUploaded = checkRequiredZones();

      if (!authenticationBagIdRef.current) {
        message.error(String(i18n.t(LOCALS.ai_identify_upload_first)));
        return;
      }

      if (!allRequiredUploaded) {
        message.error(String(i18n.t(LOCALS.ai_identify_upload_required)));
        return;
      }

      if (imagePaths.length === 0) {
        message.error(String(i18n.t(LOCALS.ai_identify_no_image_paths)));
        return;
      }

      if (!isBagMetaComplete) {
        message.error(
          String(i18n.t(LOCALS.ai_identify_fill_bag_info_required)),
        );
        return;
      }

      if (queryTimerRef.current) {
        clearTimeout(queryTimerRef.current);
        queryTimerRef.current = null;
      }
      setIsSubmitting(true);
      setAuthResult({});
      setAuthStatus('processing');
      try {
        const bagMeta = buildBagMeta();
        // Submit authentication (backend auto-saves task + starts polling)
        const submitResult = await authenticationApi.submitAuthentication({
          authenticationBagId: authenticationBagIdRef.current!,
          imagePaths,
          bagInfo: bagMeta || {},
          bagId: bagIdRef.current!,
        });

        if (submitResult?.data) {
          authenticationIdRef.current = submitResult.data.authenticationId;

          message.success(String(i18n.t(LOCALS.ai_identify_task_submitted)));

          // Start polling detail for status updates
          queryTimerRef.current = setTimeout(() => {
            fetchAuthenticationResult();
          }, AI_IDENTIFY_QUERY_INTERVAL_MS);
        }
      } catch (error: any) {
        if (!error._aiServiceHandled) {
          message.error(String(i18n.t(LOCALS.ai_identify_submit_failed)));
        }
        setAuthStatus('failed');
      } finally {
        setIsSubmitting(false);
      }
    }, [
      buildBagMeta,
      checkRequiredZones,
      collectImagePaths,
      fetchAuthenticationResult,
      isBagMetaComplete,
    ]);

    // 渲染底部操作栏
    const renderActionBar = () => {
      const imageUrls = collectImageUrls();
      const allRequiredUploaded = checkRequiredZones();
      const actionDisabled =
        imageUrls.length === 0 || !allRequiredUploaded || !isBagMetaComplete;
      const canRetryAfterCompleted =
        authStatus === 'completed' && authResult.mode === 'insufficient';

      if (canRetryAfterCompleted) {
        return (
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3 mt-4">
            <Button
              type="primary"
              loading={isSubmitting}
              disabled={actionDisabled}
              onClick={handleStartAuthentication}
            >
              {i18n.t(LOCALS.ai_identify_start)}
            </Button>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-base">
              <span className="text-gray-600">
                {i18n.t(LOCALS.authentication_ai_result)}:
              </span>
              <span className="font-medium px-4 py-1.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200 inline-flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {i18n.t(LOCALS.ai_identify_insufficient_points)}
              </span>
              <span className="text-gray-500">
                {i18n.t(LOCALS.ai_identify_insufficient_points_hint)}
              </span>
            </div>
            {authenticationBagIdRef.current && (
              <Button
                type="link"
                className="px-0"
                onClick={() => setDetailModalOpen(true)}
              >
                {i18n.t(LOCALS.view_details)}{' '}
                <ChevronRight className="w-4 h-4 inline" />
              </Button>
            )}
          </div>
        );
      }

      // 完成状态
      if (authStatus === 'completed') {
        return (
          <div className="relative flex justify-center items-center gap-4 mt-4">
            <div className="px-4 py-2 bg-gray-100 rounded-lg text-gray-600">
              {i18n.t(LOCALS.ai_identify_completed)}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">
                {i18n.t(LOCALS.authentication_ai_result)}:
              </span>
              <span
                className="font-medium px-2 py-0.5 rounded-full text-sm"
                style={
                  authResult.mode === 'few-sample-positive'
                    ? { backgroundColor: '#FFF9C4', color: '#F57F17' }
                    : getVerdictStyle(authResult.score)
                }
              >
                {authResult.verdict}
              </span>
            </div>
            {authenticationBagIdRef.current && (
              <Button
                type="link"
                className="absolute right-0 px-0"
                onClick={() => setDetailModalOpen(true)}
              >
                {i18n.t(LOCALS.view_details)}{' '}
                <ChevronRight className="w-4 h-4 inline" />
              </Button>
            )}
          </div>
        );
      }

      // 鉴定中状态
      if (authStatus === 'processing') {
        return (
          <div className="flex justify-center items-center gap-4 mt-4">
            <Button type="primary" loading>
              {i18n.t(LOCALS.ai_identify_processing)}
            </Button>
            <span className="text-gray-500">
              {i18n.t(LOCALS.ai_identify_processing_hint)}
            </span>
          </div>
        );
      }

      // 初始状态（idle 或 failed）
      return (
        <div className="flex flex-col items-center gap-2 mt-4">
          <Button
            type="primary"
            loading={isSubmitting}
            disabled={actionDisabled}
            onClick={handleStartAuthentication}
          >
            {i18n.t(LOCALS.ai_identify_start)}
          </Button>
          {!isBagMetaComplete && (
            <span className="text-orange-500 text-sm">
              {i18n.t(LOCALS.ai_identify_fill_bag_info_required)}
            </span>
          )}
          {isBagMetaComplete &&
            !allRequiredUploaded &&
            imageUrls.length > 0 && (
              <span className="text-orange-500 text-sm">
                {i18n.t(LOCALS.ai_identify_upload_required)}
              </span>
            )}
        </div>
      );
    };

    const renderBoundSummaryCard = () => {
      if (boundSummaryLoading) {
        return (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-6 py-8">
            <div className="flex items-center justify-center gap-3 text-gray-500">
              <Spin size="small" />
              <span>{i18n.t(LOCALS.bound_authentication_summary_loading)}</span>
            </div>
          </div>
        );
      }

      return (
        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/60 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-3 text-sm text-gray-700">
              <div className="text-base font-medium text-gray-900">
                {i18n.t(LOCALS.bound_authentication_summary)}
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">
                    {i18n.t(LOCALS.authentication_ai_result)}:
                  </span>
                  <span
                    className="font-medium px-2 py-0.5 rounded-full text-sm"
                    style={
                      boundSummary?.mode === 'few-sample-positive'
                        ? { backgroundColor: '#FFF9C4', color: '#F57F17' }
                        : getVerdictStyle(boundSummary?.score)
                    }
                  >
                    {boundSummary
                      ? getAuthenticationDisplayVerdictText(
                          {
                            displayVerdictKind: boundSummary.verdictKind,
                            displayVerdict: boundSummary.verdict,
                          },
                          i18n.t.bind(i18n),
                        ) || '-'
                      : '-'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">
                    {i18n.t(LOCALS.authentication_no)}:
                  </span>
                  <span className="font-medium text-gray-900">
                    {boundSummary?.displayNo || '-'}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">
                    {i18n.t(LOCALS.authentication_task_created_time)}:
                  </span>
                  <span className="font-medium text-gray-900">
                    {formatTime(boundSummary?.createdAt)}
                  </span>
                </div>
              </div>
            </div>
            <Button
              type="link"
              className="px-0 shrink-0"
              onClick={() => setDetailModalOpen(true)}
            >
              {i18n.t(LOCALS.view_details)}{' '}
              <ChevronRight className="w-4 h-4 inline" />
            </Button>
          </div>
        </div>
      );
    };

    // 渲染单个上传区域
    const renderUploadZone = (zone: PhotoZoneConfig) => {
      const zoneState = zoneImages[zone.id];
      const previewUrl = resolvePreviewUrl(zoneState);
      const hasImage = !!previewUrl;
      const isUploading = !!zoneState?.uploading;
      const zoneLabel = String(i18n.t(zone.labelKey));

      return (
        <div
          key={zone.id}
          className="relative w-[120px] h-[120px] overflow-hidden rounded"
        >
          {isUploading ? (
            <div className="w-full h-full flex items-center justify-center border border-dashed border-gray-300 rounded bg-gray-50">
              <Spin size="small" />
            </div>
          ) : hasImage ? (
            <>
              <ProxiedImage
                src={previewUrl}
                alt={zoneLabel}
                width={120}
                height={120}
                style={{ objectFit: 'cover' }}
              />
              <div
                className="flex items-center justify-center absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 cursor-pointer z-10"
                onClick={() => handleZoneDelete(zone.id)}
              >
                <X className="w-3 h-3" />
              </div>
            </>
          ) : (
            <label
              className={`relative flex flex-col justify-center items-center w-full h-full border border-dashed rounded cursor-pointer transition-colors overflow-hidden ${
                dragOverZoneId === zone.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOverZoneId(zone.id);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setDragOverZoneId(null);
                }
              }}
              onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOverZoneId(null);
                const file = e.dataTransfer.files?.[0];
                if (file && file.type.startsWith('image/')) {
                  await handleZoneUpload(zone.id, file);
                }
              }}
            >
              {zone.backgroundImage && (
                <img
                  src={zone.backgroundImage}
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain opacity-30 pointer-events-none"
                />
              )}
              <span className="relative z-10 text-sm text-gray-500 text-center px-2">
                {zone.required && <span className="text-red-500">*</span>}
                {zoneLabel}
              </span>
              <input
                type="file"
                className="absolute opacity-0 pointer-events-none"
                accept={AI_IDENTIFY_ACCEPT}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  await handleZoneUpload(zone.id, file);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
      );
    };

    // 不支持的包型提示
    if (!bagTypeConfig) {
      return (
        <div className="py-4 px-6 bg-gray-50 rounded-lg text-gray-500">
          {currentBagType ? (
            <>
              {i18n.t(LOCALS.ai_identify_unsupported_bag, {
                bagType: currentBagType,
              })}
            </>
          ) : (
            <>{i18n.t(LOCALS.ai_identify_select_category_tip)}</>
          )}
        </div>
      );
    }

    return (
      <>
        <div>
          {shouldShowBoundSummary ? (
            renderBoundSummaryCard()
          ) : (
            <>
          {/* 拍照区域网格 */}
          <Image.PreviewGroup>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className="flex flex-wrap gap-3 rounded focus:outline-none"
              tabIndex={0}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={async (e) => {
                e.preventDefault();
                setDragOverZoneId(null);
                const files = Array.from(e.dataTransfer.files).filter((f) =>
                  f.type.startsWith('image/'),
                );
                if (files.length === 0) return;
                const emptyZoneIds = (bagTypeConfig || [])
                  .filter((zone) => {
                    const s = zoneImages[zone.id];
                    return !resolvePreviewUrl(s) && !s?.uploading;
                  })
                  .map((zone) => zone.id);
                const pairs = files.slice(0, emptyZoneIds.length);
                for (let i = 0; i < pairs.length; i++) {
                  await handleZoneUpload(emptyZoneIds[i], pairs[i]);
                }
              }}
              onPaste={async (e) => {
                const images = extractClipboardImageFiles(e.nativeEvent);
                if (images.length === 0) return;
                e.preventDefault();
                const emptyZoneIds = (bagTypeConfig || [])
                  .filter((zone) => {
                    const s = zoneImages[zone.id];
                    return !resolvePreviewUrl(s) && !s?.uploading;
                  })
                  .map((zone) => zone.id);
                const pairs = images.slice(0, emptyZoneIds.length);
                for (let i = 0; i < pairs.length; i++) {
                  await handleZoneUpload(emptyZoneIds[i], pairs[i]);
                }
              }}
            >
              {bagTypeConfig.map((zone) => renderUploadZone(zone))}
            </div>
          </Image.PreviewGroup>

          {/* 操作栏 */}
          {renderActionBar()}
            </>
          )}
        </div>
        <AuthenticationDetailModal
          open={detailModalOpen}
          authenticationBagId={authenticationBagIdRef.current}
          localPreviewByOriginalName={localPreviewByOriginalName}
          onClose={() => setDetailModalOpen(false)}
        />
      </>
    );
  },
);

export default AiIdentifyPics;
